import React, { useEffect, useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
// import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  TypographyProps
} from "@mui/material";
import { Autopause } from "assets/material-custom-icons";
import { Loading } from "components/Loading/Loading";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useNavigate } from "hooks";
import { useDataRoomsActions } from "store";
import { ConnectionStatus } from "store/types";

const statusIcons: Record<ConnectionStatus["status"], JSX.Element> = {
  syncing: <CircularProgress size={18} />,
  idle: <CheckCircleIcon sx={{ fill: "#07A27D", stroke: "white", fontSize: "24px" }} />,
  // Failed: <CancelIcon sx={{ fill: "#B53232", stroke: "white", fontSize: "24px" }} />,
  pending: <Autopause sx={{ fontSize: "24px", fill: "#496682" }} />
};

const oneRowText: TypographyProps["sx"] = {
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis"
};

export const ConnectionsStatuses: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isMinimized, setIsMinimized] = useState<boolean>(true);

  const { getConnectionsStatusesAction } = useDataRoomsActions();

  const { action: getConnectionsStatuses, data, loading } = useActionHook(getConnectionsStatusesAction);
  const navigate = useNavigate();

  useEffect(() => {
    getConnectionsStatuses();
  }, []);

  useEffect(() => {
    if (!isExpanded) return;
    getConnectionsStatuses();
    const interval = setInterval(getConnectionsStatuses, 1000 * 5);

    return () => {
      clearInterval(interval);
    };
  }, [isExpanded]);

  // Minimized state - just the << button
  if (isMinimized) {
    return (
      <Stack
        sx={{
          p: "10px",
          bgcolor: "rgba(255, 255, 255, 0.1)",
          boxShadow:
            "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12);",
          position: "absolute",
          right: 0,
          bottom: "5px",
          borderRadius: "8px",
          transition: "all 0.2s ease-in-out",
          opacity: 0.3,
          "&:hover": {
            bgcolor: "rgba(255, 255, 255, 1)",
            opacity: 1
          }
        }}
      >
        <Tooltip title="Sync Status">
          <IconButton
            onClick={() => setIsMinimized(false)}
            sx={{
              color: "#071F35",
              "&:hover": {
                backgroundColor: "rgba(7, 31, 53, 0.04)"
              }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        p: "0 20px",
        bgcolor: "white",
        width: 528,
        boxShadow:
          "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12);",
        position: "absolute",
        right: 0,
        bottom: "5px",
        borderRadius: "8px"
      }}
    >
      <Accordion
        expanded={isExpanded}
        onChange={() => setIsExpanded((prevState) => !prevState)}
        sx={{
          boxShadow: "none",
          gap: "20px",
          ".MuiAccordionSummary-root": {
            display: "flex",
            gap: "6px",
            alignItems: "center",
            minHeight: 32,
            p: "10px 0"
            // maxHeight: 32,
          },
          ".MuiAccordionDetails-root": {
            display: "flex",
            flexDirection: "column",
            borderTop: "1px solid #DBE8F5",
            p: "20px 0",
            gap: " 12px",
            maxHeight: "300px",
            overflowY: "auto",
            overflowX: "hidden",
            "&.Mui.expanded": {
              m: 0
            }
          },
          ".MuiAccordionSummary-content": {
            display: "flex",
            justifyContent: "space-between",
            m: "0 !important"
          }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#071F35" }} />}>
          <Stack direction="row" sx={{ alignItems: "center", gap: "10px", width: "100%" }}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setIsMinimized(true);
              }}
              sx={{
                color: "#071F35",
                "&:hover": {
                  backgroundColor: "rgba(7, 31, 53, 0.04)"
                }
              }}
            >
              <ChevronLeftIcon sx={{ transform: "rotate(180deg)" }} />
            </IconButton>
            <Typography sx={{ color: "#071F35", fontSize: "18px", fontWeight: 400 }}>Sync Status</Typography>
            {loading ? (
              <Stack direction="row" sx={{ alignItems: "center", gap: "10px", marginLeft: "auto" }}>
                <Loading loading processTitle="Syncing" titleStyles={{ fontSize: "14px", color: "#496682" }} />
                <CircularProgress size={18} />
              </Stack>
            ) : (
              <Typography
                onClick={() => navigate({ route: "dataRoomsAllFiles" })}
                sx={{ fontSize: "15px", color: "#26A6AE", fontWeight: 400, cursor: "pointer", marginLeft: "auto" }}
              >
                Show All Files
              </Typography>
            )}
          </Stack>
        </AccordionSummary>
        {Boolean(data?.length) && (
          <AccordionDetails>
            {data?.map((item) => (
              <Stack key={item.id} direction="row" sx={{ gap: "20px" }}>
                <Stack sx={{ width: "20%", overflow: "hidden" }}>
                  <MaterialTooltip isShow title={item.type}>
                    <Typography
                      sx={{ color: "#071F35", fontSize: "16px", fontWeight: 500, lineHeight: "24px", ...oneRowText }}
                    >
                      {item.type}
                    </Typography>
                  </MaterialTooltip>
                  <MaterialTooltip isShow title={item.subdomain}>
                    <Typography
                      sx={{ color: "#496682", fontSize: "15px", fontWeight: 400, lineHeight: "20px", ...oneRowText }}
                    >
                      {item.subdomain}
                    </Typography>
                  </MaterialTooltip>
                </Stack>
                <Stack sx={{ width: "60%", overflow: "hidden" }}>
                  <MaterialTooltip isShow title={item.username}>
                    <Typography
                      sx={{ color: "#496682", fontSize: "16px", fontWeight: 400, lineHeight: "24px", ...oneRowText }}
                    >
                      {item.username}
                    </Typography>
                  </MaterialTooltip>
                  <Stack direction="row" gap={"10px"}>
                    <Typography
                      sx={{
                        color: "#071F35",
                        fontSize: "15px",
                        fontWeight: 400,
                        lineHeight: "20px",
                        ...oneRowText
                      }}
                    >
                      Recent Files
                    </Typography>
                    <Typography
                      onClick={() => navigate({ route: "dataRoomFiles", params: { room_id: item.id } })}
                      sx={{
                        color: "#26A6AE",
                        fontSize: "15px",
                        fontWeight: 400,
                        cursor: "pointer",
                        lineHeight: "20px",
                        ...oneRowText
                      }}
                    >
                      Show
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction="row" sx={{ alignItems: "center", gap: "10px" }}>
                  <Typography
                    sx={{ fontSize: "14px", color: "#496682", fontWeight: 400, lineHeight: "16px", ...oneRowText }}
                  >
                    {item.status}
                  </Typography>
                  {statusIcons[item.status]}
                </Stack>
              </Stack>
            ))}
          </AccordionDetails>
        )}
      </Accordion>
    </Stack>
  );
};
